import React from 'react';
import styled from 'styled-components';
import { MaxWidthLayoutDesktop } from '../../../templates/MaxWidthLayout';
import { attachLocale, makeExternalUrl } from '../../../../lib/utils';
import { DATA_TRACK_TYPE_INTERNAL, DOM_FOOTER } from '../../../../lib/constants';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';
import SymbolFormat from '../shared/SymbolFormat';
import ShopsLogoContainer from '../shared/ShopsLogoContainer';
import { getFooterAnalytics } from './footerAnalytics';

const FooterDiv = styled.div`
  background-color: ${p => p.theme.c.bg.primary};
  min-height: ${p => p.theme.unit(45)};
  border-top: 1px solid ${({ theme }) => theme.c.border.primary};
`;

const FooterRowDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  ${p => (p.mobile ? 'justify-content: center;' : '')}
`;

const FooterThirdRowDiv = styled(FooterRowDiv)`
  padding-top: ${p => (p.mobile ? p.theme.unit(3) : p.theme.unit(2))};
  margin-left: ${p => p.theme.unit(1.5)};
  gap: ${p => p.theme.unit(2)};
`;

const FooterAnchor = styled.a`
  ${p => p.theme.typographyRebrand.body.sm}
  color: ${p => p.theme.c.text.primary};
  text-decoration: none;
  :hover {
    text-decoration: underline;
    color: ${p => p.theme.c.text['interactive-hover']};
  }
  padding: ${p => p.theme.unit(1.5)};
`;

const LogoSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding: 6px 0;
  margin: 0 6px;
`;

const SocialImgAnchor = styled.a`
  height: ${p => p.theme.unit(4)};
  width: ${p => p.theme.unit(4)};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LogoImg = styled.img`
  width: 100%;
`;

const FooterContentDiv = styled.div`
  padding-top: ${p => (p.mobile ? p.theme.unit(4) : p.theme.unit(8))};
`;

const LogoContainer = styled.div`
  display: inline-block;
  vertical-align: top;
`;

const LogoContainerMobile = styled.div`
  display: flex;
  justify-content: center;
`;

const LinksContainer = styled.div`
  padding: 0 ${p => p.theme.unit(4)};
  display: inline-block;
  max-width: ${p => (p.mobile ? 'calc(100% - 64px)' : 'calc(100% - 302px)')};
`;

const CopyrightDiv = styled.div`
  color: ${p => p.theme.c.text.secondary};
  border-top: 1px solid ${p => p.theme.c.border.primary};
  
  p {
    ${p => p.theme.typographyRebrand.body.xs}
    white-space: pre-wrap;
    padding-bottom: 12px;
  }
`;

const CopyrightDivDesktop = styled(CopyrightDiv)`
  padding-top: ${p => p.theme.unit(3)};
  margin-top: ${p => p.theme.unit(4)};
`;

const CopyrightDivMobile = styled(CopyrightDiv)`
  padding-top: ${p => p.theme.unit(2)};
  margin-top: ${p => p.theme.unit(2)};
`;

const FooterLink = (props) => {
  const {
    locale,
    analyticsSlug,
    text,
    descriptiveText,
    url,
    StyledAnchor,
    openInNewTab,
  } = props;

  const resolvedLocaleLink = attachLocale(url, locale);
  const track = getFooterAnalytics({ analyticsSlug, text, url: resolvedLocaleLink });

  return (
    <StyledAnchor
      href={track.url}
      data-track-id={track.dataTrackingId}
      data-track-click={track.dataTrack}
      data-track-type={DATA_TRACK_TYPE_INTERNAL}
      target={openInNewTab ? '_blank' : '_self'}
      aria-label={descriptiveText}>
      {text}
    </StyledAnchor>
  );
};

const FooterSociaLink = (props) => {
  const {
    locale,
    name,
    analyticsSlug,
    url,
    openInNewTab,
    iconUrl,
    iconAltText,
  } = props;

  const resolvedLogoImageUrl = makeExternalUrl(iconUrl);
  const resolvedLocaleLink = attachLocale(url, locale);
  const track = getFooterAnalytics({ analyticsSlug, text: name, url: resolvedLocaleLink });

  return (
    <SocialImgAnchor
      href={track.url}
      data-track-id={track.dataTrackingId}
      data-track-click={track.dataTrack}
      data-track-type={DATA_TRACK_TYPE_INTERNAL}
      target={openInNewTab ? '_blank' : '_self'}
      rel="noopener noreferrer"
    >

      {iconUrl && <LogoImg src={resolvedLogoImageUrl} alt={iconAltText} />}

    </SocialImgAnchor>
  );
};

const Footer = (props) => {
  const {
    locale,
    copyrightText,
    firstRow,
    secondRow,
    thirdRow,
    airmilesLogoNavSlot,
    shopsLogoNavSlot,
  } = props;

  const logo = (
    <LogoSection>
      <ShopsLogoContainer
        airmilesLogoNavSlot={airmilesLogoNavSlot}
        shopsLogoNavSlot={shopsLogoNavSlot}
        location={`page:${DOM_FOOTER}`}
        locale={locale}
      />
    </LogoSection>
  );

  const renderedFirstRow = firstRow && firstRow.length ? firstRow.map((link, index) => (
    <FooterLink
      StyledAnchor={FooterAnchor}
      key={`${link.text}-${index}`}
      locale={locale}
      {...link} />
    )) : null;

  const renderedSecondRow = secondRow && secondRow.length ? secondRow.map((link, index) => (
    <FooterLink
      StyledAnchor={FooterAnchor}
      key={`${link.text}-${index}`}
      locale={locale}
      {...link} />
  )) : null;

  const renderedThirdRow = thirdRow && thirdRow.length ? thirdRow.map((link, index) => (
    <FooterSociaLink
      key={`${link.text}-${index}`}
      locale={locale}
      {...link} />
  )) : null;

  const FooterDesktop = (
    <footer>
      <FooterContentDiv>
        <LogoContainer>
          {logo}
        </LogoContainer>

        <LinksContainer>
          <FooterRowDiv>
            {renderedFirstRow}
          </FooterRowDiv>
          <FooterRowDiv>
            {renderedSecondRow}
          </FooterRowDiv>
          <FooterThirdRowDiv>
            {renderedThirdRow}
          </FooterThirdRowDiv>
        </LinksContainer>
      </FooterContentDiv>

      <CopyrightDivDesktop>
        <SymbolFormat linebreaks text={copyrightText} cssClassName='superscripts-legal' />
      </CopyrightDivDesktop>
    </footer>
  );

  const FooterMobile = (
    <footer>
      <FooterContentDiv>
        <LogoContainerMobile>
          {logo}
        </LogoContainerMobile>

        <LinksContainer mobile>
          <FooterRowDiv mobile>
            {renderedFirstRow}
          </FooterRowDiv>
          <FooterRowDiv mobile>
            {renderedSecondRow}
          </FooterRowDiv>
          <FooterThirdRowDiv mobile>
            {renderedThirdRow}
          </FooterThirdRowDiv>
        </LinksContainer>

        <CopyrightDivMobile>
          <SymbolFormat linebreaks text={copyrightText} cssClassName='superscripts-legal' />
        </CopyrightDivMobile>
      </FooterContentDiv>
    </footer>
  );

  const DesktopBody = () => (
    <MaxWidthLayoutDesktop>
      {FooterDesktop}
    </MaxWidthLayoutDesktop>
  );

  const MobileBody = () => (
    <MaxWidthLayoutDesktop>
      {FooterMobile}
    </MaxWidthLayoutDesktop>
  );

  const renderComp = (
    <FooterDiv>
      <MediaQueryHelper
        desktopChildren={DesktopBody()}
        mobileChildren={MobileBody()} />
    </FooterDiv>);
  return renderComp;
};

export default Footer;
